import { useEffect, useState } from 'react'
import type { VendedorDTO, WrapperResponse } from '../../../model/kartera-api-model'
import APIInvoke from '../../../utils/APIInvoke'
import {
    errorMessage,
    formatSpokenDate,
    labelize,
    personName,
    phoneHref,
    sellerContact,
    statusKey,
} from '../common/seller-common'

export interface SellerDrawerProps {
    vendedorId: number | null
    onClose: () => void
    onEdit?: (vendedorId: number) => void
    onDeleted?: (vendedor?: VendedorDTO | null) => void
}

const LOAD_ERROR = 'No se pudo cargar el detalle del vendedor.'
const DELETE_ERROR = 'No se pudo eliminar al vendedor.'

export const useSellerDrawerModel = ({ vendedorId, onClose, onDeleted }: SellerDrawerProps) => {
    const [vendedor, setVendedor] = useState<VendedorDTO | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [reloadToken, setReloadToken] = useState(0)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        if (!vendedorId) return undefined
        setVendedor(null)
        setError(null)
        setConfirmDelete(false)
        return undefined
    }, [vendedorId])

    useEffect(() => {
        if (!vendedorId) return undefined
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const response = await APIInvoke.invokeGET(`/vendedores/${vendedorId}`)
                const payload: WrapperResponse<VendedorDTO> | null = await response.json()
                if (!active) return
                if (!payload?.ok) throw new Error(payload?.message || LOAD_ERROR)
                setVendedor(payload.body)
            } catch (loadError) {
                if (active) {
                    setVendedor(null)
                    setError(errorMessage(loadError, LOAD_ERROR))
                }
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [vendedorId, reloadToken])

    const deleteVendedor = async () => {
        if (!vendedor?.id || deleting) return
        try {
            setConfirmDelete(false)
            setDeleting(true)
            const payload: WrapperResponse<VendedorDTO> | null = await (await APIInvoke.invokeDELETE(`/vendedores/${vendedor.id}`)).json()
            if (!payload?.ok) throw new Error(payload?.message || DELETE_ERROR)
            onDeleted?.(payload.body)
            onClose()
        } catch (deleteError) {
            setError(errorMessage(deleteError, DELETE_ERROR))
        } finally {
            setDeleting(false)
        }
    }

    const { email, phone, rfc } = sellerContact(vendedor)

    return {
        vendedor,
        loading,
        error,
        name: vendedor ? personName(vendedor) : '',
        activo: statusKey(vendedor?.status) === 'activo',
        statusLabel: labelize(vendedor?.status),
        sexoLabel: labelize(vendedor?.sexo),
        registroSpoken: formatSpokenDate(vendedor?.fecRegistro),
        phone,
        email,
        rfc,
        whatsapp: phoneHref(phone),
        confirmDelete,
        deleting,
        askDelete: () => setConfirmDelete(true),
        cancelDelete: () => setConfirmDelete(false),
        reload: () => setReloadToken((token) => token + 1),
        deleteVendedor,
    }
}
